import { FEATURE_CHARACTER_COSMETIC } from '../config/featureFlags';
import { PET_CONSUMABLE_CATEGORY, PET_PLAY_TICKET_CATEGORY } from '../types/petStore';

/** 상점 유형 (UI) — itemCategory를 그룹으로 묶음 */
export type StoreTypeId = 'class' | 'clothing' | 'furniture' | 'pet';

export type StoreItemCategoryId =
  | 'usage'
  | 'qr'
  | 'seatReservation'
  | 'seatTakeover'
  | 'collectible'
  | 'gacha'
  | 'cosmetic'
  | 'furniture'
  | 'roomSurface'
  | typeof PET_CONSUMABLE_CATEGORY
  | typeof PET_PLAY_TICKET_CATEGORY;

export const STORE_TYPE_CATEGORIES: Record<StoreTypeId, readonly StoreItemCategoryId[]> = {
  class: ['usage', 'qr', 'collectible', 'seatReservation', 'seatTakeover', 'gacha'],
  clothing: ['cosmetic'],
  furniture: ['furniture', 'roomSurface'],
  pet: [PET_CONSUMABLE_CATEGORY, PET_PLAY_TICKET_CATEGORY],
};

const STORE_TYPE_LABELS: Record<StoreTypeId, string> = {
  class: '교실 상점',
  clothing: '옷 상점',
  furniture: '가구 상점',
  pet: '펫 상점',
};

const STORE_TYPE_DESCRIPTIONS: Record<StoreTypeId, string> = {
  class: '사용권, QR, 좌석, 뽑기 등 학급 보상',
  clothing: '캐릭터 복장',
  furniture: '마이홈 가구·벽지·바닥',
  pet: '간식, 놀아주기 이용권',
};

/** 상점 허브 카드에 쓰는 대표 이미지와 배경 톤 */
export interface StoreTypeVisual {
  imageUrl: string;
  /** 'cover'는 배경까지 그려진 이미지, 'contain'은 투명 배경 이미지 */
  imageFit: 'cover' | 'contain';
  background: string;
}

const STORE_TYPE_VISUALS: Record<StoreTypeId, StoreTypeVisual> = {
  class: {
    imageUrl: '/images/store-hub/class.png',
    imageFit: 'cover',
    background: 'var(--Color-background-normal, #FFFFFF)',
  },
  clothing: {
    imageUrl: '/images/store-hub/clothing.png',
    imageFit: 'cover',
    background: 'var(--Color-blue-50, #E6F5FA)',
  },
  furniture: {
    imageUrl: '/images/store-hub/furniture.png',
    imageFit: 'contain',
    background: 'var(--Color-yellow-50, #FFF9E6)',
  },
  pet: {
    imageUrl: '/images/store-hub/pet.png',
    imageFit: 'contain',
    background: 'var(--Color-green-100, #B0E8D1)',
  },
};

export function getStoreTypeVisual(storeType: StoreTypeId): StoreTypeVisual {
  return STORE_TYPE_VISUALS[storeType];
}

const CATEGORY_FILTER_LABELS: Record<StoreItemCategoryId, string> = {
  usage: '사용권',
  qr: 'QR 코드',
  seatReservation: '좌석 예약',
  seatTakeover: '좌석 강탈',
  collectible: '소장용',
  gacha: '뽑기',
  cosmetic: '캐릭터 복장',
  furniture: '마이홈 가구',
  roomSurface: '벽지·바닥',
  [PET_CONSUMABLE_CATEGORY]: '캐릭터 간식',
  [PET_PLAY_TICKET_CATEGORY]: '놀아주기 이용권',
};

const STORE_TYPE_QUERY_VALUES: Record<StoreTypeId, string> = {
  class: 'class',
  clothing: 'clothing',
  furniture: 'furniture',
  pet: 'pet',
};

export function isStoreTypeId(value: string | null | undefined): value is StoreTypeId {
  return value === 'class' || value === 'clothing' || value === 'furniture' || value === 'pet';
}

/** URL ?store= 값 파싱. 없거나 유효하지 않으면 null(허브 화면) */
export function parseStoreTypeParam(value: string | null | undefined): StoreTypeId | null {
  if (isStoreTypeId(value) && isStoreTypeVisible(value)) return value;
  return null;
}

export function isStoreTypeVisible(storeType: StoreTypeId): boolean {
  if (storeType === 'clothing') return FEATURE_CHARACTER_COSMETIC;
  return true;
}

export function getVisibleStoreTypes(): Array<{ id: StoreTypeId; label: string; description: string }> {
  return (Object.keys(STORE_TYPE_LABELS) as StoreTypeId[])
    .filter(isStoreTypeVisible)
    .map((id) => ({
      id,
      label: STORE_TYPE_LABELS[id],
      description: STORE_TYPE_DESCRIPTIONS[id],
    }));
}

export function getStoreTypeLabel(storeType: StoreTypeId): string {
  return STORE_TYPE_LABELS[storeType];
}

export function getStoreTypeDescription(storeType: StoreTypeId): string {
  return STORE_TYPE_DESCRIPTIONS[storeType];
}

export function getCategoriesForStoreType(storeType: StoreTypeId): StoreItemCategoryId[] {
  return [...STORE_TYPE_CATEGORIES[storeType]];
}

export function getCategoryFilterOptions(
  storeType: StoreTypeId
): Array<{ key: 'all' | StoreItemCategoryId; label: string }> {
  const categories = getCategoriesForStoreType(storeType);
  if (categories.length <= 1) {
    return [];
  }
  return [
    { key: 'all', label: '전체' },
    ...categories.map((key) => ({ key, label: CATEGORY_FILTER_LABELS[key] })),
  ];
}

export function itemBelongsToStoreType(
  itemCategory: string | undefined,
  storeType: StoreTypeId
): boolean {
  if (!itemCategory) return false;
  // 레거시 좌석 카테고리
  if (storeType === 'class' && itemCategory === 'seat') return true;
  return (STORE_TYPE_CATEGORIES[storeType] as readonly string[]).includes(itemCategory);
}

export function getDefaultCategoryForStoreType(storeType: StoreTypeId): StoreItemCategoryId {
  return STORE_TYPE_CATEGORIES[storeType][0];
}

export function getItemCategoryOptionsForStoreType(
  storeType: StoreTypeId
): Array<{ label: string; value: StoreItemCategoryId }> {
  return getCategoriesForStoreType(storeType).map((value) => ({
    label: CATEGORY_FILTER_LABELS[value],
    value,
  }));
}

export function storeTypeToQueryValue(storeType: StoreTypeId): string {
  return STORE_TYPE_QUERY_VALUES[storeType];
}

export function countItemsByStoreType(
  items: Array<{ itemCategory?: string }>,
  storeType: StoreTypeId
): number {
  return items.filter((item) => itemBelongsToStoreType(item.itemCategory, storeType)).length;
}
